import React, { useState, useEffect } from "react";
import { X, Share2, Copy, MessageCircle, Mail, Users, Check, CheckSquare, Square, Star, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareListModal = ({ list, onClose, userId }) => {
  const items = list?.items || [];
  const [selected, setSelected] = useState(new Set(items.map((_, i) => i)));
  const [shareMode, setShareMode] = useState(null); // null | 'app' 
  const [searchEmail, setSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [sending, setSending] = useState(false);
  const [savedContacts, setSavedContacts] = useState([]);

  // Load saved contacts from localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("share_contacts") || "[]");
      setSavedContacts(stored);
    } catch { setSavedContacts([]); }
  }, []);

  const saveContact = (contact) => {
    setSavedContacts((prev) => {
      if (prev.some((c) => c.email === contact.email)) return prev;
      const updated = [contact, ...prev].slice(0, 10);
      localStorage.setItem("share_contacts", JSON.stringify(updated));
      return updated;
    });
  };

  const removeContact = (email) => {
    setSavedContacts((prev) => {
      const updated = prev.filter((c) => c.email !== email);
      localStorage.setItem("share_contacts", JSON.stringify(updated));
      return updated;
    });
  };

  const allSelected = selected.size === items.length;

  const toggleItem = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map((_, i) => i)));
  };

  const getSelectedItems = () => items.filter((_, i) => selected.has(i));

  const formatListText = () => {
    const sel = getSelectedItems();
    const header = `${list.name} (${sel.length} items)`;
    const lines = sel.map((item) => {
      let line = `- ${item.name}`;
      if (item.quantity > 1) line += ` x${item.quantity}`;
      if (item.estimated_price) line += ` (~$${item.estimated_price.toFixed(2)})`;
      return line;
    });
    return `${header}\n${lines.join("\n")}\n\nShared via Foodz Lizts`;
  };

  const handleNativeShare = async () => {
    const text = formatListText();
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      try {
        await navigator.share({ title: list.name, text });
        toast.success("List shared!");
        onClose();
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }

    // Fallback: clipboard
    try {
      await navigator.clipboard.writeText(text);
      toast.success("List copied to clipboard!");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("List copied to clipboard!");
    }
    onClose();
  };

  const handleEmailShare = () => {
    const text = formatListText();
    const subject = encodeURIComponent(`Grocery List: ${list.name}`);
    const body = encodeURIComponent(text);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
    onClose();
  };

  const handleSmsShare = () => {
    const text = formatListText();
    const body = encodeURIComponent(text);
    window.open(`sms:?body=${body}`, "_self");
    onClose();
  };

  const handleSearchUser = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setFoundUser(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/grocery/share/find-user?email=${encodeURIComponent(searchEmail.trim())}`);
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          setFoundUser(data.user);
        } else {
          toast.error("No user found with that email");
        }
      }
    } catch {
      toast.error("Search failed");
    }
    setSearching(false);
  };

  const handleSendToUser = async () => {
    if (!foundUser) return;
    setSending(true);
    try {
      const sel = getSelectedItems();
      const res = await fetch(`${BACKEND_URL}/api/grocery/share/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_user_id: userId,
          to_user_id: foundUser.id,
          list_name: list.name,
          items: sel.map((item) => ({
            name: item.name,
            quantity: item.quantity || 1,
            category: item.category || "",
            estimated_price: item.estimated_price || null,
          })),
        }),
      });
      if (res.ok) {
        saveContact({ name: foundUser.name, email: foundUser.email, id: foundUser.id });
        toast.success(`List sent to ${foundUser.name}!`);
        onClose();
      } else {
        toast.error("Failed to send");
      }
    } catch {
      toast.error("Failed to send");
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" data-testid="share-list-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">Share Grocery List</h3>
            <p className="text-xs text-gray-500">{selected.size} of {items.length} items selected</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full" data-testid="close-share-modal">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Item selection */}
        <div className="px-4 pt-3 pb-2">
          <button
            onClick={toggleAll}
            className="flex items-center gap-2 text-sm text-violet-600 font-medium mb-2"
            data-testid="select-all-items"
          >
            {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            {allSelected ? "Deselect All" : "Select All"}
          </button>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {items.map((item, i) => (
              <button
                key={item.id || i}
                onClick={() => toggleItem(i)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
                  selected.has(i) ? "bg-violet-50 text-gray-900" : "bg-gray-50 text-gray-400"
                }`}
                data-testid={`share-item-${i}`}
              >
                {selected.has(i) ? <Check className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" /> : <div className="h-3.5 w-3.5 rounded border border-gray-300 flex-shrink-0" />}
                <span className={item.completed ? "line-through" : ""}>{item.name}</span>
                {item.quantity > 1 && <Badge className="ml-auto text-xs bg-gray-100 text-gray-500">x{item.quantity}</Badge>}
              </button>
            ))}
          </div>
        </div>

        {/* Share options */}
        <div className="px-4 py-3 space-y-2 border-t border-gray-100">
          {!shareMode && (
            <>
              <Button
                className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                onClick={handleNativeShare}
                disabled={selected.size === 0}
                data-testid="share-native"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share via Messages / Apps
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleSmsShare}
                  disabled={selected.size === 0}
                  data-testid="share-sms"
                >
                  <MessageCircle className="h-3.5 w-3.5 mr-1" />
                  Text
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleEmailShare}
                  disabled={selected.size === 0}
                  data-testid="share-email"
                >
                  <Mail className="h-3.5 w-3.5 mr-1" />
                  Email
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={handleNativeShare}
                  disabled={selected.size === 0}
                  data-testid="share-copy"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  Copy
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={() => setShareMode("app")}
                disabled={selected.size === 0}
                data-testid="share-in-app"
              >
                <Users className="h-4 w-4 mr-2" />
                Send to a Foodz Lizts User
              </Button>
            </>
          )}

          {shareMode === "app" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button onClick={() => { setShareMode(null); setFoundUser(null); setSearchEmail(""); }} className="text-xs text-gray-500 hover:text-gray-700">
                  Back
                </button>
                <span className="text-sm font-medium text-gray-700">Send to App User</span>
              </div>

              {/* Saved contacts */}
              {savedContacts.length > 0 && (
                <div data-testid="saved-contacts">
                  <p className="text-xs text-gray-400 mb-1.5">Recent</p>
                  <div className="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
                    {savedContacts.map((c) => (
                      <div
                        key={c.email}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 hover:bg-emerald-50 transition-colors group"
                      >
                        <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {c.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <button
                          className="flex-1 text-left min-w-0"
                          onClick={() => { setFoundUser({ id: c.id, name: c.name, email: c.email }); setSearchEmail(c.email); }}
                          data-testid={`saved-contact-${c.email}`}
                        >
                          <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                          <p className="text-xs text-gray-400 truncate">{c.email}</p>
                        </button>
                        <button
                          onClick={() => removeContact(c.email)}
                          className="p-1 text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          data-testid={`remove-contact-${c.email}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchUser()}
                  placeholder="Enter their email..."
                  className="text-sm flex-1"
                  data-testid="share-user-email"
                />
                <Button size="sm" onClick={handleSearchUser} disabled={searching || !searchEmail.trim()} data-testid="share-search-user">
                  {searching ? "..." : "Find"}
                </Button>
              </div>
              {foundUser && (
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold">
                    {foundUser.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{foundUser.name}</p>
                    <p className="text-xs text-gray-500">{foundUser.email}</p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    onClick={handleSendToUser}
                    disabled={sending}
                    data-testid="share-send-btn"
                  >
                    {sending ? "Sending..." : "Send"}
                  </Button>
                </div>
              )}
              <p className="text-xs text-gray-400">They'll see the items in their grocery list when they open the app.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareListModal;
